import React from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import AnimatedSection from '@/components/AnimatedSection';
import styles from './Process.module.scss';

const processStages = [
  {
    num: '01',
    id: 'discover',
    title: 'DISCOVER',
    heading: 'Understand the business, goals, audience, and project requirements.',
    details: ['BUSINESS GOALS', 'USER NEEDS', 'PROJECT REQUIREMENTS'],
    align: 'left',
  },
  {
    num: '02',
    id: 'plan',
    title: 'PLAN',
    heading: 'Define the product direction, technical approach, scope, and priorities.',
    details: ['PRODUCT DIRECTION', 'TECH APPROACH', 'SCOPE & PRIORITIES'],
    align: 'right',
  },
  {
    num: '03',
    id: 'design',
    title: 'DESIGN',
    heading: 'Create the user experience, interface, visual direction, and product structure.',
    details: ['USER EXPERIENCE', 'INTERFACE DESIGN', 'VISUAL DIRECTION'],
    align: 'left',
  },
  {
    num: '04',
    id: 'build',
    title: 'BUILD',
    heading: 'Develop, integrate, test, and refine the product.',
    details: ['DEVELOPMENT', 'TESTING', 'ITERATION'],
    align: 'right',
  },
  {
    num: '05',
    id: 'launch',
    title: 'LAUNCH',
    heading: 'Prepare the product for deployment and release.',
    details: ['DEPLOYMENT', 'RELEASE', 'GO-LIVE'],
    align: 'left',
  },
  {
    num: '06',
    id: 'grow',
    title: 'GROW',
    heading: 'Continue improving, optimizing, and supporting the digital product.',
    details: ['OPTIMIZATION', 'PERFORMANCE', 'GROWTH'],
    align: 'right',
  },
];

const principles = [
  {
    num: '01',
    title: 'REMOTE-FIRST',
    desc: 'We work globally and stay flexible across time zones.',
  },
  {
    num: '02',
    title: 'CLEAR COMMUNICATION',
    desc: 'Regular updates and transparent progress tracking.',
  },
  {
    num: '03',
    title: 'SINGLE POINT OF CONTACT',
    desc: 'One person responsible for keeping everything moving.',
  },
  {
    num: '04',
    title: 'DOCUMENTED EVERYTHING',
    desc: 'Decisions, systems, and handoffs stay clear.',
  },
];

const Process: React.FC = () => {
  return (
    <IonPage id="process-page">
      <IonContent fullscreen scrollY scrollEvents={true}>
        <div className="page-wrapper">
          <Navbar />

          {/* 1. HERO SECTION */}
          <section className={`${styles.hero} dark-section`}>
            <div className="container">
              <AnimatedSection delay={0}>
                <span className={styles.eyebrow}>How We Work</span>
              </AnimatedSection>
              
              <AnimatedSection delay={0.08}>
                <h1 className={styles.heroTitle}>
                  From Idea to<br />
                  <span className={styles.gradient}>Digital Product.</span>
                </h1>
              </AnimatedSection>
              
              <AnimatedSection delay={0.16}>
                <p className={styles.heroSub}>
                  We turn ideas into purposeful digital products through a clear, collaborative process.
                </p>
              </AnimatedSection>
              
              <AnimatedSection delay={0.24}>
                <div className={styles.processIndicator}>
                  <span>DISCOVER</span>
                  <div className={styles.indicatorLine} />
                  <span>PLAN</span>
                  <div className={styles.indicatorLine} />
                  <span>DESIGN</span>
                  <div className={styles.indicatorLine} />
                  <span>BUILD</span>
                  <div className={styles.indicatorLine} />
                  <span>LAUNCH</span>
                  <div className={styles.indicatorLine} />
                  <span>GROW</span>
                </div>
              </AnimatedSection>
            </div>
          </section>

          {/* 2. MAIN PROCESS JOURNEY */}
          <section className={`${styles.journeySection} dark-section`}>
            <div className="container">
              <div className={styles.journeyContainer}>
                {/* Connecting Line */}
                <div className={styles.journeyLine}>
                  <div className={styles.journeyLineFill} />
                </div>
                
                {processStages.map((stage, i) => (
                  <div key={stage.id} className={`${styles.stageRow} ${styles[stage.align]}`}>
                    <AnimatedSection 
                      delay={0.1} 
                      direction={stage.align === 'left' ? 'right' : 'left'} 
                      className={styles.stageContent}
                    >
                      <div className={styles.stageNum}>{stage.num}</div>
                      <div className={styles.stageInfo}>
                        <h2 className={styles.stageTitle}>{stage.title}</h2>
                        <h3 className={styles.stageHeading}>"{stage.heading}"</h3>
                        
                        <ul className={styles.stageDetails}>
                          {stage.details.map((detail, j) => (
                            <li key={j}>{detail}</li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Subtle Radar/Node */}
                      <div className={styles.stageNode}>
                        <div className={styles.nodeCore} />
                        <div className={styles.nodeRing} />
                      </div>
                    </AnimatedSection>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 3. WHAT YOU GET */}
          <section className={`${styles.whatYouGetSection} light-section`}>
            <div className="container">
              <AnimatedSection>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionLabel}>What You Get</span>
                  <h2 className={styles.sectionHeading}>
                    Not just a product.<br />
                    A foundation to grow on.
                  </h2>
                </div>
              </AnimatedSection>
              
              <div className={styles.benefitsLayout}>
                <AnimatedSection delay={0.1}>
                  <div className={styles.benefitItem}>
                    <h3>CLEAR DIRECTION</h3>
                    <p>A defined product strategy before development begins.</p>
                  </div>
                </AnimatedSection>
                <AnimatedSection delay={0.2}>
                  <div className={styles.benefitItem}>
                    <h3>PURPOSEFUL DESIGN</h3>
                    <p>Interfaces built around real users and real goals.</p>
                  </div>
                </AnimatedSection>
                <AnimatedSection delay={0.3}>
                  <div className={styles.benefitItem}>
                    <h3>SOLID ENGINEERING</h3>
                    <p>Scalable, maintainable technology.</p>
                  </div>
                </AnimatedSection>
                <AnimatedSection delay={0.4}>
                  <div className={styles.benefitItem}>
                    <h3>LONG-TERM THINKING</h3>
                    <p>Support and optimization beyond launch.</p>
                  </div>
                </AnimatedSection>
              </div>
            </div>
          </section>

          {/* 4. OUR PRINCIPLES */}
          <section className={`${styles.principlesSection} dark-section`}>
            <div className="container">
              <AnimatedSection>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionLabel}>Our Principles</span>
                  <h2 className={styles.sectionHeading}>How we collaborate.</h2>
                </div>
              </AnimatedSection>
              
              <div className={styles.principlesList}>
                {principles.map((p, i) => (
                  <AnimatedSection key={p.num} delay={i * 0.1} direction="up">
                    <div className={styles.principleRow}>
                      <span className={styles.principleNum}>{p.num}</span>
                      <div className={styles.principleText}>
                        <h3>{p.title}</h3>
                        <p>{p.desc}</p>
                      </div>
                      <ArrowRight className={styles.principleIcon} />
                      <div className={styles.principleHoverLine} />
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </section>

          {/* 5. THE ARKATECHS LOOP */}
          <section className={`${styles.loopSection} dark-section`}>
            <div className="container">
              <AnimatedSection>
                <h2 className={styles.loopHeading}>
                  BUILD.<br />
                  LEARN.<br />
                  IMPROVE.<br />
                  REPEAT.
                </h2>
              </AnimatedSection>
              
              <AnimatedSection delay={0.2} direction="fade">
                <div className={styles.loopVisual}>
                  <div className={styles.loopRing}></div>
                  <div className={styles.loopRingInner}></div>
                  <div className={styles.loopSteps}>
                    <span>DISCOVER</span>
                    <ArrowRight size={14} />
                    <span>BUILD</span>
                    <ArrowRight size={14} />
                    <span>LAUNCH</span>
                    <ArrowRight size={14} />
                    <span>LEARN</span>
                    <ArrowRight size={14} />
                    <span>GROW</span>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </section>

          {/* 6. FINAL CTA */}
          <section className={`${styles.ctaSection} dark-section`}>
            <div className={styles.ctaRings}>
              <div className={styles.ring1} />
              <div className={styles.ring2} />
              <div className={styles.ring3} />
            </div>
            
            <div className={`container ${styles.ctaContainer}`}>
              <AnimatedSection>
                <h2 className={styles.ctaHeading}>
                  Got an idea<br />
                  <span className={styles.gradient}>worth building?</span>
                </h2>
              </AnimatedSection>
              <AnimatedSection delay={0.1}>
                <p className={styles.ctaSub}>Let's turn it into something real.</p>
              </AnimatedSection>
              <AnimatedSection delay={0.2}>
                <Link to="/contact" className={styles.ctaLink}>
                  START A CONVERSATION <ArrowRight size={18} />
                  <div className={styles.ctaLine} />
                </Link>
              </AnimatedSection>
            </div>
          </section>

          <Footer />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Process;
